// src/stores/charactersStore.ts
import { makeAutoObservable, runInAction } from 'mobx';
import dashboardStore from './dashboardStore';
import sceneStore from './sceneStore';
import { ICharacterDto } from '../shared/interfaces';
import CharactersApiService from '../services/api/CharactersApiService';

interface LevelRecord {
    recordId: string;
    level: string;
    typeEx: string;
    name: string;
    createdAt: string;
}

class CharactersStore {
    public isLoading = false;
    public characters: ICharacterDto[] = [];
    public selectedCharacter: ICharacterDto | null = null;
    public characterDataCSV: any = [];
    public characterDataGesture: any = [];
    public statResult: any = [];
    public currentLevel: string = '';
    public currentAttempt: number = 1;
    public selectedCharacterDate: string | null = null;
    public currentCreatedAt: string | null = null;
    public searchValue: string = '';
    public availableLevels: LevelRecord[] = [];
    public completedQuests: any[] = [];
    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    get filteredCharacters() {
        const search = this.searchValue.toLowerCase();
        return this.characters.filter((char) =>
            char.name.toLowerCase().includes(search)
        );
    }

    public async getCharacters() {
        this.isLoading = true;
        try {
            const data = await CharactersApiService.getCharacters();
            runInAction(() => {
                this.characters = data.map((c: any) => ({
                    id: c.ID ?? c.id,
                    userId: c.UserID ?? c.userId ?? 0,
                    name: c.Name ?? c.name ?? 'Unknown',
                    level: c.Level ?? c.level ?? 1,
                    race: c.Race ?? c.race ?? 'Unknown',
                    charClass: c.CharClass ?? c.charClass ?? 'Unknown',
                    gender: c.Gender ?? c.gender ?? 'Unknown',
                    guild: c.Guild ?? c.guild ?? '',
                    portrait: c.Portrait ?? c.portrait ?? c.Image ?? c.image ?? '',
                    image: c.Image ?? c.image ?? '',
                    updatedAt: c.UpdatedAt ?? c.updatedAt ?? new Date().toISOString(),
                    csvFiles: c.CSVFiles ?? c.csvFiles ?? [],
                }));
            });
        } catch (e) {
            console.error('Error fetching characters:', e);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    public selectCharacter(characterId: number) {
        this.selectedCharacter = this.characters.find((c) => c.id === characterId) || null;
        this.characterDataCSV = [];
        this.characterDataGesture = [];
        this.statResult = [];
        this.selectedCharacterDate = null;
        this.currentCreatedAt = null;
        this.currentAttempt = 1;
        this.currentLevel = '';
        this.availableLevels = [];
        this.completedQuests = [];
    }

    public selectLevel(levelId: number | string, recordId?: string) {
        this.currentLevel = String(levelId);
        this.currentCreatedAt = recordId ?? null;
    }

    public async loadCharacterData(level: string) {
        if (!this.selectedCharacter) return;

        this.isLoading = true;
        dashboardStore.clearSelectedJoint();

        try {
            this.currentLevel = level;

            const [csvData, gestureData, statData] = await Promise.all([
                CharactersApiService.getCharacterCSV(this.selectedCharacter.id, level).catch((err) => {
                    if (err.response?.status === 404) return [];
                    throw err;
                }),
                CharactersApiService.getCharacterGesture(this.selectedCharacter.id, level).catch((err) => {
                    if (err.response?.status === 404) return [];
                    throw err;
                }),
                CharactersApiService.getCharacterStat(this.selectedCharacter.id, level).catch((err) => {
                    if (err.response?.status === 404) return [];
                    throw err;
                }),
            ]);

            runInAction(() => {
                this.characterDataCSV = csvData;
                this.characterDataGesture = gestureData;
                this.statResult = statData;
            });
        } catch (e) {
            console.error('Error loading character data:', e);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /** 🔹 Загрузка CSV через recordId */
    public async loadProcessedCSVForLevel(recordId: string) {
        if (!this.selectedCharacter) return;

        this.isLoading = true;

        try {
            const csvData = await CharactersApiService.getCharacterCSVByLevel(
                this.selectedCharacter.id,
                '', // дата не нужна
                undefined,
                undefined,
                undefined,
                'processed',
                recordId
            );

            console.log('🔹 fetchProcessedCSV data for recordId', recordId, csvData);

            runInAction(() => {
                this.characterDataCSV = csvData;
            });
        } catch (e: any) {
            if (e.response?.status === 404) {
                console.warn('⚠ CSV не найден для recordId:', recordId);
                runInAction(() => (this.characterDataCSV = []));
            } else {
                console.error('Ошибка загрузки processed CSV:', e);
                runInAction(() => (this.characterDataCSV = []));
            }
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    public selectCsvDate(date: string) {
        if (!this.selectedCharacter) return;

        this.selectedCharacterDate = date;
        const csvFile = this.selectedCharacter.csvFiles?.find((f) => f.csvDate === date);
        if (csvFile) {
            runInAction(() => {
                this.characterDataCSV = csvFile.csvData;
            });
        }
    }

    public deInit() {
        this.characters = [];
        this.selectedCharacter = null;
        this.characterDataCSV = [];
        this.characterDataGesture = [];
        this.statResult = [];
        this.currentLevel = '';
        this.currentCreatedAt = null;
        this.currentAttempt = 1;
        this.selectedCharacterDate = null;
        this.searchValue = '';
        this.availableLevels = [];
    }

    /** 🔹 Загрузка доступных уровней с recordId */
    public async loadLevelsForDate(date: string = new Date().toISOString().split('T')[0]) {
        if (!this.selectedCharacter) return;

        this.isLoading = true;

        try {
            const filesData = await CharactersApiService.getCharacterFilesByDate(
                this.selectedCharacter.id,
                date
            );

            runInAction(() => {
                this.availableLevels = filesData.files.map((f: any, idx: number) => ({
                    recordId: f.recordID || f.recordId || `${idx}-${Date.now()}`,
                    level: f.level || 'unknown',
                    typeEx: f.typeEx || 'unknown',
                    name: `Уровень ${f.level || 'unknown'}`,
                    createdAt: f.createdAt || new Date().toISOString(),
                }));
            });
        } catch (e) {
            console.error('Ошибка загрузки уровней персонажа:', e);
            runInAction(() => {
                this.availableLevels = [];
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }


    public async loadCompletedQuests(date: string) {
        if (!this.selectedCharacter) return;

        this.isLoading = true;
        console.log(`🔹 Загрузка завершённых квестов для персонажа ${this.selectedCharacter.name} на дату ${date}...`);

        try {
            // Вызываем API
            const data = await CharactersApiService.getCompletedQuests(
                this.selectedCharacter.id,
                date
            );

            runInAction(() => {
                // Маппим данные: всегда boolean для Started
                this.completedQuests = data.map((q: any) => ({
                    ...q,
                    Started: q.Started ?? false, // если null или undefined → false
                    Completed: q.Completed ?? false,
                    Active: q.Active ?? true,
                    Visible: q.Visible ?? true,
                    TimedPoints: q.TimedPoints ? q.TimedPoints : [], // пустой массив, если нет
                }));
            });

            console.log(`✅ Загружено ${data.length} завершённых квестов для персонажа ${this.selectedCharacter.name}`);
        } catch (err) {
            console.error('❌ Ошибка загрузки завершённых квестов:', err);
            runInAction(() => {
                this.completedQuests = [];
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }



}

export default new CharactersStore();
